import { useState, useRef } from 'react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { IUserModel } from '@packages/global';
import { useTranslation } from 'react-i18next';
import ImageUpload from '../../../../common/ImageUpload';
import MediaPreviewModal from '../../../../common/MediaPreviewModal';

interface ReferenceImagesSectionProps {
  referenceImages: string[];
  canAddMore: boolean;
  maxCount: number;
  isDisabled: boolean;
  projectId: string;
  episodeId: string;
  models: IUserModel[];
  showUpload: boolean;
  onUploadToggle: (show: boolean) => void;
  onAddImage: (url: string) => void;
  onRemoveImage: (index: number) => void;
  onReorder: (newImages: string[]) => void;
}

export default function ReferenceImagesSection({
  referenceImages,
  canAddMore,
  maxCount,
  isDisabled,
  projectId,
  episodeId,
  models,
  showUpload,
  onUploadToggle,
  onAddImage,
  onRemoveImage,
  onReorder
}: ReferenceImagesSectionProps) {
  const { t } = useTranslation('editor');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = (index: number) => {
    dragIndexRef.current = index;
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = dragIndexRef.current;
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragOverIndex(null);
      return;
    }
    const newImages = [...referenceImages];
    const [moved] = newImages.splice(dragIndex, 1);
    newImages.splice(dropIndex, 0, moved);
    onReorder(newImages);
    dragIndexRef.current = null;
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setDragOverIndex(null);
  };

  return (
    <div className="px-4 pt-3">
      <div className="flex gap-2 flex-wrap items-center relative">
        {referenceImages.map((url, index) => (
          <div
            key={index}
            className={`relative group cursor-grab active:cursor-grabbing transition-opacity ${dragOverIndex === index ? 'opacity-50' : 'opacity-100'}`}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
          >
            <img
              src={url}
              alt={t('imageGenForm.refImageAlt', { index: index + 1 })}
              className="w-12 h-12 object-cover rounded-lg border border-gray-700 hover:opacity-80 transition-opacity"
              onClick={() => setPreviewImage(url)}
              draggable={false}
            />
            <button
              type="button"
              onClick={() => onRemoveImage(index)}
              disabled={isDisabled}
              className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors disabled:opacity-50"
            >
              <XMarkIcon className="h-2.5 w-2.5" />
            </button>
          </div>
        ))}

        <button
          onClick={() => onUploadToggle(true)}
          disabled={isDisabled || !canAddMore}
          className="w-12 h-12 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center justify-center transition-colors disabled:opacity-50 border-2 border-dashed border-gray-600"
          title={canAddMore ? t('imageGenForm.addRefImage') : t('common.maxImages', { max: maxCount })}
        >
          <PlusIcon className="h-5 w-5 text-gray-400" />
        </button>

        {showUpload && canAddMore && (
          <div className="absolute bottom-full left-0 mb-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-20 p-3 min-w-[280px]">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-gray-400">
                {t('imageGenForm.refImageCount', { current: referenceImages.length, max: maxCount })}
              </span>
              <button
                onClick={() => onUploadToggle(false)}
                className="text-gray-500 hover:text-gray-300"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <ImageUpload
              projectId={projectId}
              episodeId={episodeId}
              onUploadSuccess={onAddImage}
              models={models}
            />
          </div>
        )}
      </div>

      <MediaPreviewModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        type="image"
        src={previewImage || ''}
        alt={t('imageGenForm.refImagePreview')}
      />
    </div>
  );
}
